import React, {useEffect, useState} from "react";
import InputForm from "../../../../../components/input/input-form.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaPlus, FaTrash} from "react-icons/fa";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import ContactIdService from "../../../../../services/web/contact-id-service.js";
import Swal from "sweetalert2";
import ScheduleModalAlarm from "./modal-alarm-schedule.jsx";
import projectUtils from "../../../../../utils/project-utils.js";

const partitionOptions = [
    { value: "a", label: "A" },
    { value: "b", label: "B" },
    { value: "c", label: "C" },
    { value: "d", label: "D" },
    { value: "e", label: "E" },
    { value: "f", label: "F" }
];

const emptyMatrix = () =>
    Array.from({ length: 7 }, () => Array(24).fill(0));

const AlarmPartitions = ({ id, partitions = [], setPartitions, driver, ipConfig, portConfig, setIsLoading, setAccountsRemoved }) => {
    const [openSchedule, setOpenSchedule] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);

    useEffect(() => {
        if (!partitions || partitions.length === 0) {
            setPartitions([
                {
                    account: "",
                    partition: "a",
                    details: "",
                    password: "",
                    isPrimary: true,
                    alarmActive: emptyMatrix(),
                    isNew: true
                }
            ]);
        }
    }, []);

    const driverValue =
        typeof driver === "string" ? driver : driver?.value;

    const addPartition = () => {
        if (driverValue?.toUpperCase() !== "INTELBRAS") {
            projectUtils.swalMessage("warning", "", "Apenas para fabricante Intelbras.");
            return;
        }

        const used = partitions.map(p => p.partition);
        const next = partitionOptions.find(o => !used.includes(o.value));

        if (!next) {
            projectUtils.swalMessage("warning", "", "Número máximo de partições atingido.");
            return;
        }

        setPartitions([
            ...partitions,
            {
                account: "",
                partition: next.value,
                details: "",
                password: "",
                isPrimary: next.value === "a",
                alarmActive: emptyMatrix(),
                isNew: true
            }
        ]);
    };

    const updatePartition = (index, field, value) => {
        const updated = [...partitions];

        if (field === "account") {
            value = value?.toUpperCase();

            const duplicated = updated.some(
                (p, i) => i !== index && p.account === value
            );

            if (duplicated) {
                projectUtils.swalMessage("warning", "", 'Esta "Conta" já está sendo utilizada.');
                return;
            }
        }

        if (field === "password") {
            value = value.slice(0, 6);
        }

        if (field === "partition") {
            if (updated[index].isPrimary) return;

            updated.forEach(p => (p.isPrimary = false));
            updated[index].isPrimary = value === "a";
        }

        updated[index] = {
            ...updated[index],
            [field]: value
        };

        setPartitions(updated);
    };

    const removePartition = (index) => {
        if (partitions[index]?.isPrimary) return;

        const partition = partitions[index];

        if (partition?.id) {
            setAccountsRemoved(prev => [
                ...prev,
                partition.account
            ]);
        }

        setPartitions(
            partitions.filter((_, i) => i !== index)
        );
    };

    const getAvailablePartitions = (currentIndex) => {
        const used = partitions
            .filter((_, i) => i !== currentIndex)
            .map(p => p.partition);

        return partitionOptions.filter(o => !used.includes(o.value));
    };

    const handleAlarmTest = async (p) => {
        const mainAccount = partitions.find(x => x.isPrimary)?.account;

        if (!p.account || !p.partition || !ipConfig || !portConfig || !mainAccount) {
            projectUtils.swalErrorMessage("Erro", "Preencha todos os campos obrigatórios.");
            return;
        }

        const confirm = await Swal.fire({
            title: "Atenção",
            text: "A central poderá armar ou desarmar durante o teste.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Confirmar",
            confirmButtonColor: "#063A6C",
            cancelButtonText: "Cancelar",
            reverseButtons: true
        });

        if (!confirm.isConfirmed) return;

        try {
            setIsLoading(true);
            await ContactIdService.alarmTest({
                function: "test",
                partition: p.partition?.toUpperCase(),
                account: p.account,
                mainAccount,
                ip: ipConfig,
                port: portConfig,
                password: p.password
            }).then((response) => {
                response?.data?.error
                    ? projectUtils.swalErrorMessage("", response.data.message)
                    : projectUtils.swalSuccessMessage("success", "", "Teste realizado com sucesso", "Ok");
            });
        } catch {
            projectUtils.swalErrorMessage("", "Erro ao testar alarme.")
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="mt-4">
            <h4 className="font-semibold mb-3 border-b border-gray-200">
                Partições
            </h4>

            {[...partitions]
                .sort((a, b) => a.partition.localeCompare(b.partition))
                .map((p) => {
                    const index = partitions.findIndex(
                        item => item.partition === p.partition
                    );

                    return (
                        <div key={p.partition} className="grid grid-cols-12 gap-4 mb-3">
                            <InputForm
                                id={`partition_account-${index}`}
                                label="Conta *"
                                value={p.account}
                                onChange={e => updatePartition(index, "account", e.target.value)}
                                classNameWrapper="col-span-2"
                                disabled={id && !p.isNew}
                            />

                            <SelectComponent
                                label="Partição *"
                                value={partitionOptions.find(o => o.value === p.partition)}
                                options={getAvailablePartitions(index)}
                                onChange={opt => updatePartition(index, "partition", opt.value)}
                                isDisabled={p.isPrimary}
                                isClearable={false}
                                classNameWrapper="col-span-1"
                            />

                            <InputForm
                                id={`partition_name-${index}`}
                                label="Nome"
                                value={p.details}
                                onChange={e => updatePartition(index, "details", e.target.value)}
                                classNameWrapper="col-span-5"
                            />

                            <InputForm
                                id={`partition_password-${index}`}
                                label="Senha"
                                type="password"
                                value={p.password}
                                onChange={e => updatePartition(index, "password", e.target.value)}
                                classNameWrapper="col-span-2"
                                disabled={
                                    driver?.toUpperCase() === "VISONIC" ||
                                    driver?.toUpperCase() === "JFL" ||
                                    driver?.toUpperCase() === "RADIONGE"
                                }
                            />

                            <div className="col-span-2 flex gap-2 items-end">
                                <Button
                                    target="primary"
                                    onClick={() => {
                                        setSelectedIndex(index);
                                        setOpenSchedule(true);
                                    }}
                                    className={"flex-1"}
                                >
                                    Horários
                                </Button>

                                <Button
                                    target="secondary"
                                    onClick={() => handleAlarmTest(p)}
                                >
                                    Testar
                                </Button>

                                <Button
                                    target="danger"
                                    onClick={() => removePartition(index)}
                                    disabled={p.isPrimary}
                                >
                                    <FaTrash />
                                </Button>
                            </div>
                        </div>
                    );
                })}

            <Button onClick={addPartition}>
                <FaPlus /> Adicionar partição
            </Button>

            <ScheduleModalAlarm
                open={openSchedule}
                setOpen={setOpenSchedule}
                alarmActive={partitions[selectedIndex]?.alarmActive}
                onSave={matrix => {
                    const updated = [...partitions];
                    updated[selectedIndex] = {
                        ...updated[selectedIndex],
                        alarmActive: matrix
                    };
                    setPartitions(updated);
                }}
            />
        </div>
    );
};

export default AlarmPartitions;